// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {memo, useEffect} from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

import {useEventCallback} from 'usehooks-ts';

import {getEmojiImageUrl, isSystemEmoji} from 'mattermost-redux/utils/emoji_utils';
import {type Emoji} from 'mattermost-redux/types/emojis';
import {type EmojiCursor} from 'components/emoji_picker/types';
import {EMOJI_SCROLL_THROTTLE_DELAY, TRANSPARENT_PIXEL} from 'components/emoji_picker/constants';

interface Props {
    emoji: Emoji;
    rowIndex: number;
    colIndex: number;
    isSelected?: boolean;
    onClick: (emoji: Emoji, rowIndex: number, colIndex: number) => void;
    onMouseOver: (cursor: EmojiCursor) => void;
}

function EmojiPickerItem({emoji, rowIndex, colIndex, isSelected, onClick, onMouseOver}: Props) {
    const {formatMessage} = useIntl();

    const handleMouseOver = () => {
        if (!isSelected) {
            let emojiId = '';
            if (isSystemEmoji(emoji)) {
                emojiId = emoji.unified;
            } else {
                emojiId = emoji.id;
            }
            onMouseOver({rowIndex, emojiId, emoji});
        }
    };

    const throttledMouseOver = useEventCallback(
        throttle(handleMouseOver, EMOJI_SCROLL_THROTTLE_DELAY, {
            leading: true,
            trailing: false,
        }));

    const handleClick = () => {
        onClick(emoji, rowIndex, colIndex);
    };

    const itemClassName = classNames('emoji-picker__item', {
        selected: isSelected,
    });

    const imgRef = React.useRef<HTMLImageElement>(null);

    let content;

    if (isSystemEmoji(emoji)) {
        const emojiName = emoji.short_name ? emoji.short_name : emoji.name;
        const emojiUnified = emoji.unified ? emoji.unified.toLowerCase() : emoji.name.toLowerCase();

        content = (
            <img
                alt={'emoji image'}
                data-testid={emoji.short_names}
                src={TRANSPARENT_PIXEL}
                className={`emojisprite emoji-category-${emoji.category} emoji-${emojiUnified}`}
                loading='lazy'
                id={`emoji-${emojiUnified}`}
                aria-label={formatMessage(
                    {
                        id: 'emoji_picker_item.emoji_aria_label',
                        defaultMessage: '{emojiName} emoji',
                    },
                    {
                        emojiName: (emojiName).replace(/_/g, ' '),
                    },
                )}
                role='button'
            />
        );
    } else {
        content = (
            <img
                ref={imgRef}
                alt={'custom emoji image'}
                data-testid={emoji.name}
                src={getEmojiImageUrl(emoji)}
                className={'emoji-category--custom'}
                loading='lazy'
            />
        );
    }

    useEffect(() => {
        const img = imgRef.current;

        return () => {
            if (img) {
                img.setAttribute('src', '');
            }
        };
    }, []);

    return (
        <div
            className={itemClassName}
            onClick={handleClick}
            onMouseOver={throttledMouseOver}
        >
            <div data-testid='emojiItem'>
                {content}
            </div>
        </div>
    );
}

function areEqual(prevProps: Props, nextProps: Props) {
    return (
        prevProps.isSelected === nextProps.isSelected
    );
}

export default memo(EmojiPickerItem, areEqual);
